<script setup>
/**
 * ReadingActionsMenu — the choices at the end of a reading, sized to the choices.
 *
 * These options used to live inside the chat window, which meant a two-option
 * ending reserved a whole conversation pane: composer, message area, and a
 * large empty region above the pills. The reading above paid for that space.
 *
 * This is the same set of actions with none of the conversation chrome. It
 * measures itself and reports its height, so the pane can be exactly as tall
 * as the options are — "more or less options" is the normal case, so nothing
 * here is a fixed height.
 *
 * Presentational: the host owns what each action does, the rating widget, and
 * the login gate.
 */
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import RatingCrystals from '@/components/ui/RatingCrystals.vue'
import ReadingEndRating from '@/components/ui/ReadingEndRating.vue'
import { store } from '@/store'
import { loginCtaLabelFor } from '@/utils/reading-lock'
import { giftAction } from '@/utils/care-package'
import { peopleReadingActions } from '@/utils/stage-four'
import { talkActionLabel } from '@/utils/talk-it-through'

const props = defineProps({
  // [{ button_label, action_type }] — the same shape the chat received.
  actions: { type: Array, default: () => [] },
  // Rating is inline and re-tappable; the host owns whether it's open.
  rateOpen: { type: Boolean, default: false },
  // The reading's one rating (SP-R1), above the options.
  endRating: { type: Boolean, default: false },
  // Rate/Share belong to the logged-in seeker. A logged-out viewer of a shared
  // reading gets one login CTA in their place.
  seekerLoggedIn: { type: Boolean, default: true },
  // The rating just landed (3+): SHARE is the call to action, glowed once.
  shareLanded: { type: Boolean, default: false },
})
const emit = defineEmits(['select', 'request-login', 'measured', 'reading-rated'])

const rootEl = ref(null)

// Rate + Share are the two that need an account behind them.
const OWNED_BY_SEEKER = new Set(['rate_reading', 'share_reading'])

// A gift reading's Share is "Gift to Lark", and once sent "Read Lark's gift".
function labelFor(a) {
  if (a && a.action_type === 'share_reading') {
    const ga = giftAction(store.reading_gift, { sendIt: !!store.intro_people_reading })
    if (ga) return ga.label
  }
  // Talk it through's paid continue carries its price.
  return talkActionLabel(a, { pricesVisible: store.prices_visible !== false })
}

// Stage 4 (recipient journey R7.5): her reading for someone ends on one
// choice, "Send it to Mom".
const visible = computed(() => {
  const list = (props.actions || []).filter(a => a && a.button_label)
  return store.intro_people_reading && list.some(a => a.action_type === 'share_reading')
    ? peopleReadingActions(list) : list
})

const gated = computed(() =>
  !props.seekerLoggedIn
  && visible.value.some(a => OWNED_BY_SEEKER.has(a.action_type)))

const shown = computed(() =>
  gated.value
    ? visible.value.filter(a => !OWNED_BY_SEEKER.has(a.action_type))
    : visible.value)

// The action that ends the reading reads as the primary move; the rest are
// quieter. Nothing else is styled specially — the list is data, not a design.
const PRIMARY = new Set(['exit_reading', 'finish_reading'])
const isPrimary = (a) => PRIMARY.has(a.action_type)

// Three or more options stacked one-per-row push the reading below the fold on
// a short screen. Two-up halves the rows, and these labels are short enough to
// sit side by side. Below three there is nothing to gain, so the full-width
// row stays — it is the easier tap target.
const twoUp = computed(() => shown.value.length + (gated.value ? 1 : 0) >= 3)

/** Report our real height so the host can fit the pane to it. */
function measure() {
  const el = rootEl.value
  if (!el) return
  emit('measured', Math.ceil(el.getBoundingClientRect().height))
}
onMounted(() => nextTick(measure))
// Options stream in, and the rating panel opens beneath them — both change the
// height the pane should be.
watch(() => [shown.value.length, props.rateOpen, props.endRating], () => nextTick(measure))
</script>

<template>
  <div class="ram" ref="rootEl">
    <ReadingEndRating v-if="endRating" @rated="n => { emit('reading-rated', n); nextTick(measure) }" />
    <div class="ram-list" :class="{ 'ram-list--two-up': twoUp }"
         role="group" aria-label="Reading options">
      <button
        v-for="(a, i) in shown"
        :key="i"
        type="button"
        class="ram-btn"
        :class="{ 'ram-btn--primary': isPrimary(a), 'ram-btn--landed': shareLanded && a.action_type === 'share_reading' }"
        @click="emit('select', a)"
      >{{ labelFor(a) }}</button>

      <button v-if="gated" type="button" class="ram-btn ram-btn--primary"
              @click="emit('request-login')">
        {{ loginCtaLabelFor({ rate: visible.some(a => a.action_type === 'rate_reading'), linkHolder: !!store.link_holder }) }}
      </button>
    </div>

    <RatingCrystals v-if="rateOpen" />
  </div>
</template>

<style scoped>
/* Sized by content, never by the pane. The host reads our height back and
   fits the split to it, so any padding here is real cost — keep it lean. */
.ram {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px calc(14px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  /* A long list scrolls inside the menu rather than pushing the reading up.
     The reading is the thing the seeker paid for; the options are how they
     leave it. */
  max-height: 60dvh;
  overflow-y: auto;
  background: #14071f;
}
.ram-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ram-list--two-up {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
/* An odd last option spans both columns rather than leaving a hole. */
.ram-list--two-up > :last-child:nth-child(odd) { grid-column: 1 / -1; }
.ram-list--two-up .ram-btn { font-size: 13px; padding: 10px 12px; }
.ram-btn {
  width: 100%;
  min-height: 44px;              /* thumb-sized, per the app's other action rows */
  padding: 11px 18px;
  border-radius: 999px;
  border: 1px solid rgba(240, 217, 163, 0.28);
  background: rgba(255, 255, 255, 0.05);
  color: #e9dcff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.ram-btn:hover { border-color: #f0d9a3; background: rgba(255, 255, 255, 0.09); }
.ram-btn:focus-visible { outline: 2px solid #f0d9a3; outline-offset: 2px; }
.ram-btn--primary {
  background: linear-gradient(180deg, #7b4bb0, #4e2776);
  border-color: rgba(240, 217, 163, 0.55);
  color: #fff;
}
.ram-btn--primary:hover { filter: brightness(1.1); background: linear-gradient(180deg, #7b4bb0, #4e2776); }

/* Wide screens have room to sit the options side by side, which keeps the pane
   one row tall instead of one row per option. */
@media (min-width: 700px) {
  .ram-list,
  .ram-list--two-up {
    display: flex; flex-direction: row; flex-wrap: wrap; justify-content: center;
  }
  .ram-btn { width: auto; min-width: 160px; flex: 0 1 auto; }
}

/* A landed rating (3+): the bar's SHARE gets one soft gold glow, once. */
.ram-btn--landed {
  animation: share-landed-glow 1.8s ease-out 1 both;
}
@keyframes share-landed-glow {
  0%   { box-shadow: 0 0 0 0 rgba(231, 201, 131, 0); }
  35%  { box-shadow: 0 0 0 7px rgba(231, 201, 131, .38), 0 0 22px 4px rgba(231, 201, 131, .35); }
  100% { box-shadow: 0 0 0 0 rgba(231, 201, 131, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .ram-btn--landed { animation: none; border-color: #f0d9a3; box-shadow: 0 0 0 2px rgba(231, 201, 131, .55); }
}
</style>
